import React, { useEffect, useState } from 'react';
import AccountDropdown from './AccountDropdown';
import { apiFetch } from './api';
import { fmtCount, fmtDollar, fmtMillions, fmtPct } from './intelligence/shared';

const KPI_FORMATTERS = {
  count: fmtCount,
  dollar: fmtDollar,
  millions: fmtMillions,
  pct: fmtPct,
};

function formatKpiValue({ value, fmt }) {
  const formatter = fmt ? KPI_FORMATTERS[fmt] : null;
  return formatter ? formatter(value) : value;
}

function EnlaceIntelligencePreview() {
  const [kpis, setKpis] = useState([]);
  const [memberCount, setMemberCount] = useState(null);

  useEffect(() => {
    apiFetch('/api/dashboards/cost-intelligence/overview')
      .then(r => r.json())
      .then(data => setKpis(data.kpis ?? []))
      .catch(() => {});

    apiFetch('/api/dashboards/intelligence-config')
      .then(r => r.json())
      .then(data => setMemberCount(data.membership_count))
      .catch(() => {});
  }, []);

  const members = memberCount != null ? fmtCount(memberCount) : '—';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
      <div style={{ fontSize: '0.7rem', color: 'var(--eh-text-mid)', letterSpacing: '0.03em' }}>
        Q1 2026&nbsp;&nbsp;&middot;&nbsp;&nbsp;{members} Members&nbsp;&nbsp;&middot;&nbsp;&nbsp;All Markets&nbsp;&nbsp;&middot;&nbsp;&nbsp;Commercial
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {kpis.map(kpi => (
          <div key={kpi.label} style={{
            flex: '1 1 calc(33% - 0.4rem)',
            minWidth: 80,
            background: '#f8fafc',
            border: '1px solid var(--eh-border)',
            borderRadius: 6,
            padding: '0.4rem 0.55rem',
          }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--eh-navy)', lineHeight: 1.1 }}>{formatKpiValue(kpi)}</div>
            <div style={{ fontSize: '0.62rem', color: 'var(--eh-text-mid)', textTransform: 'uppercase', letterSpacing: '0.03em', marginTop: 2 }}>{kpi.label}</div>
            <div style={{ fontSize: '0.65rem', fontWeight: 600, marginTop: 2, color: kpi.up ? '#198754' : '#dc3545' }}>{kpi.delta}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


function VBPPreview() {
  const [programs, setPrograms] = useState([]);
  useEffect(() => {
    apiFetch('/api/vbp/programs').then(r => r.json()).then(setPrograms).catch(() => {});
  }, []);

  if (programs.length === 0) {
    return <span className="text-muted fst-italic" style={{ fontSize: '0.85rem' }}>No programs configured</span>;
  }

  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>
      {programs.map((p, i) => (
        <li key={p.id} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0.4rem 0',
          borderBottom: i < programs.length - 1 ? '1px solid var(--eh-border)' : 'none',
          fontSize: '0.85rem', color: 'var(--eh-text-dark)',
        }}>
          <span style={{
            width: 22, height: 22, borderRadius: '50%',
            background: 'var(--eh-navy)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
          }}>{i + 1}</span>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)', flexShrink: 0 }}>{p.contractYear}</span>
        </li>
      ))}
    </ul>
  );
}

function ContractsPreview() {
  const [contracts, setContracts] = useState([]);
  useEffect(() => {
    apiFetch('/api/contracts').then(r => r.json()).then(setContracts).catch(() => {});
  }, []);

  const STATUS_COLORS = {
    Draft: '#6c757d', Active: '#198754',
    Expired: '#dc3545', Terminated: '#dc3545', 'Pending Renewal': '#fd7e14',
  };

  if (contracts.length === 0) {
    return <span className="text-muted fst-italic" style={{ fontSize: '0.85rem' }}>No contracts configured</span>;
  }

  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>
      {contracts.slice(0, 5).map((c, i) => (
        <li key={c.id} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '0.4rem 0',
          borderBottom: i < Math.min(contracts.length, 5) - 1 ? '1px solid var(--eh-border)' : 'none',
          fontSize: '0.82rem', color: 'var(--eh-text-dark)',
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: STATUS_COLORS[c.status] ?? '#6c757d',
          }} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {c.name || <em style={{ opacity: 0.6 }}>Untitled</em>}
          </span>
          <span style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)', flexShrink: 0 }}>{c.contractYear}</span>
        </li>
      ))}
    </ul>
  );
}

function UsersPreview() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    apiFetch('/api/users').then(r => r.json()).then(data => setCount(data.length)).catch(() => {});
  }, []);

  return (
    <div className="d-flex flex-column align-items-center justify-content-center w-100">
      <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--eh-navy)', lineHeight: 1 }}>
        {count ?? '\u2014'}
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 6 }}>
        {count === 1 ? 'User' : 'Users'}
      </div>
    </div>
  );
}

export default function LandingPage({ onNavigate, currentUser, onLogout }) {
  const tiles = [
    { key: 'dashboard',   title: 'Enlace Intelligence',  preview: <EnlaceIntelligencePreview />, navigateTo: 'dashboard'   },
    { key: 'vbp',         title: 'Value-Based Programs', preview: <VBPPreview />,               navigateTo: 'vbp'         },
    { key: 'contracts',   title: 'Contracts',             preview: <ContractsPreview />,          navigateTo: 'contracts'   },
    { key: 'users',       title: 'User Management',       preview: <UsersPreview />,              navigateTo: 'users'       },
  ];

  return (
    <div className="d-flex flex-column min-vh-100" style={{ background: 'var(--eh-bg-page)' }}>
      <nav className="navbar navbar-dark bg-primary px-4 py-2">
        <span className="navbar-brand mb-0 d-flex align-items-center gap-3">
          <img src="/enlace-logo-white.svg" alt="Enlace Health" style={{ height: 36 }} />
          <span style={{ fontWeight: 300, opacity: 0.65, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff' }}>
            Home
          </span>
        </span>
        <AccountDropdown currentUser={currentUser} onLogout={onLogout} />
      </nav>
      <main className="flex-grow-1 p-4">
        <h6 className="text-uppercase mb-4" style={{ color: 'var(--eh-text-mid)', letterSpacing: '0.08em', fontWeight: 700 }}>
          Select a module
        </h6>
        <div className="row g-4">
          {tiles.map(tile => (
            <div key={tile.key} className="col-12 col-md-6">
              <div
                className="card h-100 shadow-sm landing-tile"
                onClick={() => onNavigate(tile.navigateTo)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-header fw-semibold">{tile.title}</div>
                <div className="card-body d-flex align-items-center justify-content-center">
                  {tile.preview}
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
